import { DayLabel, TableActions, TableContainer } from '@shared/components'
import { useLocales } from '@shared/locales'
import { Flex, TableColumnsType } from 'antd'
import { MenuProps } from 'antd/lib'
import { Edit2, Eye, Trash } from 'iconsax-react'
import { useMemo, useState } from 'react'

import { AddEditProduct } from './AddEditProduct'
import { DeleteProduct } from './DeleteProduct'
import { mockProducts } from './mockData'
import { ProductDetailsModal } from './ProductDetailsModal'
import { ProductsHeader } from './ProductsHeader'
import { ActionsType, Product } from './types'

type ModalType = 'add' | 'edit' | 'delete' | 'details'

const modalTypes: Record<ModalType, React.ComponentType<ActionsType>> = {
  add: AddEditProduct,
  edit: AddEditProduct,
  delete: DeleteProduct,
  details: ProductDetailsModal
}

const formatPrice = (price: number, currency = 'MDL') => {
  return `${price.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ${currency}`
}

export const ProductsColTrendsetter = () => {
  const { t } = useLocales()
  const [products, setProducts] = useState<Product[]>(mockProducts)
  const [modalState, setModalState] = useState<{
    type: ModalType | null
    product: Product | null
  }>({ product: null, type: null })

  const actionItems = (product: Product): MenuProps['items'] => [
    {
      label: t('generic.preview'),
      key: 'details',
      icon: <Eye size={20} />,
      onClick: () => setModalState({ product, type: 'details' })
    },
    {
      type: 'divider'
    },
    {
      label: t('buttons.edit'),
      key: 'edit',
      icon: <Edit2 size={20} />,
      onClick: () => setModalState({ product, type: 'edit' })
    },
    {
      type: 'divider'
    },
    {
      label: t('buttons.delete'),
      key: 'delete',
      danger: true,
      icon: <Trash size={20} />,
      onClick: () => setModalState({ product, type: 'delete' })
    }
  ]

  const columns: TableColumnsType<Product> = [
    {
      title: t('products_trendsetter.format_label'),
      dataIndex: 'format',
      key: 'format',
      render: (format: Product['format']) =>
        t(`products_trendsetter.format.${format}`)
    },
    {
      title: t('products_trendsetter.location_name'),
      dataIndex: 'location_name',
      key: 'location_name'
    },
    {
      title: t('products_trendsetter.address'),
      dataIndex: 'address',
      key: 'address'
    },
    {
      title: t('products_trendsetter.internal_code'),
      dataIndex: 'internal_code',
      key: 'internal_code',
      render: (code) => code || '-'
    },
    {
      title: t('products_trendsetter.start_date'),
      dataIndex: 'start_date',
      key: 'start_date',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: t('products_trendsetter.end_date'),
      dataIndex: 'end_date',
      key: 'end_date',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: t('products_trendsetter.net_cost'),
      dataIndex: 'net_cost',
      key: 'net_cost',
      render: (cost) => formatPrice(cost)
    },
    {
      title: t('products_trendsetter.gross_cost'),
      dataIndex: 'gross_cost',
      key: 'gross_cost',
      render: (cost) => formatPrice(cost)
    },
    {
      title: t('products_trendsetter.supplier_name'),
      dataIndex: 'supplier_name',
      key: 'supplier_name'
    },
    {
      title: t('filter.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at) => <DayLabel date={created_at} />
    },
    {
      width: 50,
      key: 'actions',
      fixed: 'right',
      render: (_, product) => <TableActions options={actionItems(product)} />
    }
  ]

  const actionComponent = useMemo(() => {
    if (!modalState.type) return null

    const Component = modalTypes[modalState.type]

    const handleSuccess = (updatedProducts?: Product[]) => {
      if (updatedProducts && updatedProducts.length > 0) {
        // If editing, replace the product; if adding, append it
        if (modalState.product) {
          setProducts(
            products.map((p) =>
              p.id === updatedProducts[0].id ? updatedProducts[0] : p
            )
          )
        } else {
          setProducts([...products, updatedProducts[0]])
        }
      } else if (modalState.type === 'delete' && modalState.product) {
        // Delete case
        setProducts(products.filter((p) => p.id !== modalState.product!.id))
      }
      setModalState({ type: null, product: null })
    }

    return (
      <Component
        open
        product={modalState.product ?? undefined}
        onCancel={() => setModalState({ type: null, product: null })}
        onSuccess={handleSuccess}
      />
    )
  }, [modalState.type, modalState.product, products])

  const handleRowClick = (record: Product) => {
    setModalState({ product: record, type: 'details' })
  }

  return (
    <Flex vertical gap={16}>
      <TableContainer
        rowKey="id"
        columns={columns}
        dataSource={products}
        loading={false}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' }
        })}
        rightChildren={
          <ProductsHeader
            onAdd={() => setModalState({ type: 'add', product: null })}
          />
        }
      />

      {actionComponent}
    </Flex>
  )
}
